import { DtcFaultDescriptionForThingResponse, DtcFaultDescriptionResponse, DtcSourceDescriptionResponse } from '@eagle/core-data-types';
import { DtcTypes, GenericLampState, MalfunctionIndicatorState } from '@eagle/data-function-types';
import { Skeleton } from '@ethings/ui-toolkit';
import { FC, ReactNode } from 'react';
import { useAuthenticated } from '../../auth';
import { usePromise } from '../../hooks';
import { FieldLoadError } from '../field';

type SelectablePath<T> = {
  [Key in keyof T]: NonNullable<T[Key]> extends ReactNode ? Key : never;
}[keyof T];

interface CommonProps {
  errorComponent?: ReactNode;
  loadingComponent?: ReactNode;
}

interface FaultLampProps {
  amberWarning?: GenericLampState;
  malfunctionIndicator?: MalfunctionIndicatorState;
  redStop?: GenericLampState;
}

interface FaultDescriptionForBrandVariantProps extends CommonProps, FaultLampProps {
  brandCode: string;
  path: SelectablePath<DtcFaultDescriptionResponse>;
  dtcBrandVariantId: number;
  dtcType: DtcTypes;
  fmi: number;
  language: string;
  method: 'dtc-fault-description-for-brand-variant';
}

interface FaultDescriptionForThingProps extends CommonProps, FaultLampProps {
  path: SelectablePath<DtcFaultDescriptionForThingResponse>;
  dtcCode: string;
  dtcType: DtcTypes;
  fmi: number;
  language: string;
  method: 'dtc-fault-description-for-thing';
  source: number;
  thingId: string;
}

interface SourceDescriptionForSourceProps extends CommonProps {
  brandCode: string;
  dtcType: DtcTypes;
  path: SelectablePath<DtcSourceDescriptionResponse>;
  dtcSourceId: number;
  language: string;
  method: 'dtc-source-description-for-source';
}

interface SourceDescriptionForThingProps extends CommonProps {
  path: SelectablePath<DtcSourceDescriptionResponse>;
  dtcCode: string;
  dtcType: DtcTypes;
  fmi: number;
  language: string;
  method: 'dtc-source-description-for-thing';
  source: number;
  thingId: string;
}

export type RenderFaultProps =
  | FaultDescriptionForBrandVariantProps
  | FaultDescriptionForThingProps
  | SourceDescriptionForSourceProps
  | SourceDescriptionForThingProps;

type FaultResponse = DtcFaultDescriptionForThingResponse | DtcFaultDescriptionResponse | DtcSourceDescriptionResponse;

export const RenderFault: FC<RenderFaultProps> = (props) => {
  const { restClient } = useAuthenticated();
  const {
    errorComponent = <FieldLoadError />,
    loadingComponent = <Skeleton />,
    method,
    path,
  } = props;

  const brandCode = 'brandCode' in props ? props.brandCode : undefined;
  const dtcBrandVariantId = 'dtcBrandVariantId' in props ? props.dtcBrandVariantId : undefined;
  const dtcCode = 'dtcCode' in props ? props.dtcCode : undefined;
  const dtcType = 'dtcType' in props ? props.dtcType : undefined;
  const dtcSourceId = 'dtcSourceId' in props ? props.dtcSourceId : undefined;
  const fmi = 'fmi' in props ? props.fmi : undefined;
  const language = props.language;
  const source = 'source' in props ? props.source : undefined;
  const thingId = 'thingId' in props ? props.thingId : undefined;
  const amberWarning = 'amberWarning' in props ? props.amberWarning : undefined;
  const malfunctionIndicator = 'malfunctionIndicator' in props ? props.malfunctionIndicator : undefined;
  const redStop = 'redStop' in props ? props.redStop : undefined;

  const [result, , state] = usePromise<FaultResponse>(async () => {
    switch (method) {
      case 'dtc-fault-description-for-brand-variant':
        return restClient.dtc.getDtcFaultDescriptionForBrandVariant(
          dtcBrandVariantId as number,
          brandCode as string,
          language,
          {
            dtcType,
            amberWarning,
            fmi: fmi as number,
            malfunctionIndicator,
            redStop,
          },
        );
      case 'dtc-fault-description-for-thing':
        return restClient.dtc.getDtcFaultDescriptionForThing({
          amberWarning,
          dtcCode: dtcCode as string,
          dtcType,
          fmi: fmi as number,
          language,
          malfunctionIndicator,
          redStop,
          source: source as number,
          thingId: thingId as string,
        });
      case 'dtc-source-description-for-source':
        return restClient.dtc.getDtcSourceDescriptionForSource(
          dtcSourceId as number,
          brandCode as string,
          language,
          { dtcType },
        );
      case 'dtc-source-description-for-thing':
        return restClient.dtc.getDtcSourceDescriptionForThing({
          dtcCode: dtcCode as string,
          dtcType,
          fmi: fmi as number,
          language,
          source: source as number,
          thingId: thingId as string,
        });
    }
  }, [
    amberWarning,
    brandCode,
    dtcBrandVariantId,
    dtcCode,
    dtcType,
    dtcSourceId,
    fmi,
    language,
    malfunctionIndicator,
    method,
    redStop,
    restClient,
    source,
    thingId,
  ]);

  if (state === 'pending') return loadingComponent;
  if (state === 'rejected' || !result) return errorComponent;

  const content = result[path as keyof FaultResponse] as ReactNode;
  return <>{content}</>;
};
